import {
	ActionIcon,
	Group,
	Menu,
	Table,
	Text,
	Tooltip,
	Badge,
	UnstyledButton,
	Center,
} from "@mantine/core";
import {
	IconEdit,
	IconTrash,
	IconDownload,
	IconMail,
	IconFileAnalytics,
	IconCalendarX,
	IconChevronUp,
	IconChevronDown,
	IconSelector,
	IconSend,
	IconHistory,
} from "@tabler/icons-react";
import type { Account } from "../../store/accounts/accountsSlice";

export type SortDirection = "asc" | "desc" | null;
export type AccountSortField = "name" | "contactEmail" | "maxSeats" | null;

interface AccountTableProps {
	accounts: Account[];
	currentUserRole?: string;
	sortField?: AccountSortField;
	sortDirection?: SortDirection;
	onSort?: (field: AccountSortField) => void;
	onEditAccount: (account: Account) => void;
	onDeleteAccount: (id: string, name: string) => void;
	onExportAccount: (id: string, name: string) => void;
	onEmailExport: (id: string, name: string) => void;
	onShowBillingHistory: (id: string, name: string) => void;
	onExportStatusReport: (id: string, name: string) => void;
	onEmailStatusReport: (id: string, name: string) => void;
	onSendToSubscribers: (
		accountId: string,
		accountName: string,
		reportType: "billing" | "status"
	) => void;
	onRemoveScheduledReport?: (
		accountId: string,
		email: string,
		accountName: string
	) => void;
}

interface ThSortableProps {
	children: React.ReactNode;
	sorted: boolean;
	reversed: boolean;
	onSort: () => void;
}

function ThSortable({ children, sorted, reversed, onSort }: ThSortableProps) {
	const Icon = sorted
		? reversed
			? IconChevronUp
			: IconChevronDown
		: IconSelector;
	return (
		<Table.Th>
			<UnstyledButton
				onClick={onSort}
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "4px 0",
					cursor: "pointer",
				}}>
				<Text
					fw={500}
					size="sm">
					{children}
				</Text>
				<Center style={{ width: 21, height: 21 }}>
					<Icon
						size={16}
						stroke={1.5}
					/>
				</Center>
			</UnstyledButton>
		</Table.Th>
	);
}

function AccountTable({
	accounts,
	currentUserRole,
	sortField,
	sortDirection,
	onSort,
	onEditAccount,
	onDeleteAccount,
	onExportAccount,
	onEmailExport,
	onShowBillingHistory,
	onExportStatusReport,
	onEmailStatusReport,
	onSendToSubscribers,
	onRemoveScheduledReport,
}: AccountTableProps) {
	// Matches PUT and DELETE /api/accounts/:id, which both allow managers
	const canModifyAccounts = ["admin", "manager"].includes(currentUserRole || "");
	const canViewAccounts = ["admin", "manager", "viewer"].includes(
		currentUserRole || ""
	);
	const canSendReports = ["admin", "manager"].includes(currentUserRole || "");

	const handleSort = (field: AccountSortField) => {
		if (onSort) {
			onSort(field);
		}
	};

	return (
		<Table.ScrollContainer
			minWidth={500}
			maxHeight={500}>
			<Table
				striped
				highlightOnHover>
				<Table.Thead>
					<Table.Tr>
						<ThSortable
							sorted={sortField === "name"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("name")}>
							Name
						</ThSortable>
						<ThSortable
							sorted={sortField === "contactEmail"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("contactEmail")}>
							Contact Email
						</ThSortable>
						<ThSortable
							sorted={sortField === "maxSeats"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("maxSeats")}>
							Max Seats
						</ThSortable>
						<Table.Th>Actions</Table.Th>
					</Table.Tr>
				</Table.Thead>
				<Table.Tbody>
					{accounts.map((account) => (
						<Table.Tr key={account._id}>
							<Table.Td>
								<div>
									<Group
										gap="xs"
										align="center">
										<Text fw={500}>{account.name}</Text>
										{account.scheduledReports &&
											account.scheduledReports.length > 0 && (
												<Badge
													size="sm"
													color="blue"
													variant="light"
													leftSection={<IconCalendarX size={12} />}>
													{account.scheduledReports.length} scheduled
												</Badge>
											)}
									</Group>
									{account.description && (
										<Text
											size="sm"
											c="dimmed">
											{account.description}
										</Text>
									)}
								</div>
							</Table.Td>
							<Table.Td>{account.contactEmail}</Table.Td>
							<Table.Td>
								<Text size="sm">
									{account.maxSeats === -1 ? "Unlimited" : account.maxSeats}
								</Text>
							</Table.Td>
							<Table.Td>
								{(canModifyAccounts || canViewAccounts) && (
									<Group gap="xs">
										{canViewAccounts && (
											<Menu
												shadow="md"
												width={260}>
												<Menu.Target>
													<Tooltip label="Export Reports">
														<ActionIcon
															variant="light"
															color="violet">
															<IconFileAnalytics size={16} />
														</ActionIcon>
													</Tooltip>
												</Menu.Target>
												<Menu.Dropdown>
													<Menu.Label>Billing Reports</Menu.Label>
													<Menu.Item
														leftSection={<IconDownload size={16} />}
														onClick={() =>
															onExportAccount(account._id, account.name)
														}>
														Export Billing Report
													</Menu.Item>
													<Menu.Item
														leftSection={<IconHistory size={16} />}
														onClick={() =>
															onShowBillingHistory(account._id, account.name)
														}>
														Past Billing Reports
													</Menu.Item>
													{canSendReports && (
														<Menu.Item
															leftSection={<IconMail size={16} />}
															onClick={() =>
																onEmailExport(account._id, account.name)
															}>
															Email Billing Report
														</Menu.Item>
													)}
													<Menu.Divider />
													<Menu.Label>Status Reports</Menu.Label>
													<Menu.Item
														leftSection={<IconDownload size={16} />}
														onClick={() =>
															onExportStatusReport(account._id, account.name)
														}>
														Export Status Report
													</Menu.Item>
													{canSendReports && (
														<Menu.Item
															leftSection={<IconMail size={16} />}
															onClick={() =>
																onEmailStatusReport(account._id, account.name)
															}>
															Email Status Report
														</Menu.Item>
													)}
													{canSendReports &&
														account.scheduledReports &&
														account.scheduledReports.length > 0 && (
															<>
																<Menu.Divider />
																<Menu.Label>Quick Send</Menu.Label>
																<Menu.Item
																	leftSection={<IconSend size={16} />}
																	onClick={() =>
																		onSendToSubscribers(
																			account._id,
																			account.name,
																			"billing"
																		)
																	}>
																	Send Billing Report to{" "}
																	{account.scheduledReports.length}{" "}
																	subscriber(s)
																</Menu.Item>
																<Menu.Item
																	leftSection={<IconSend size={16} />}
																	onClick={() =>
																		onSendToSubscribers(
																			account._id,
																			account.name,
																			"status"
																		)
																	}>
																	Send Status Report to{" "}
																	{account.scheduledReports.length}{" "}
																	subscriber(s)
																</Menu.Item>
															</>
														)}
													{account.scheduledReports &&
														account.scheduledReports.length > 0 &&
														onRemoveScheduledReport && (
															<>
																<Menu.Divider />
																<Menu.Label>Scheduled Reports</Menu.Label>
																{account.scheduledReports.map((report) => (
																	// Viewers see who gets the report, but
																	// removing one is admin/manager only - the
																	// backend answered them 403
																	<Menu.Item
																		key={report._id}
																		leftSection={<IconCalendarX size={14} />}
																		color={canModifyAccounts ? "red" : undefined}
																		onClick={
																			canModifyAccounts
																				? () =>
																						onRemoveScheduledReport(
																							account._id,
																							report.email,
																							account.name
																						)
																				: undefined
																		}>
																		<div style={{ maxWidth: "160px" }}>
																			<Text
																				size="xs"
																				fw={500}
																				truncate
																				title={report.email}>
																				{report.email}
																			</Text>
																			<Text
																				size="xs"
																				c="dimmed">
																				{/* A missed month shows up here */}
																				{report.lastSentAt
																					? `Last sent ${new Date(report.lastSentAt).toLocaleDateString("he-IL")}`
																					: "Not sent yet"}
																				{canModifyAccounts && " · click to remove"}
																			</Text>
																		</div>
																	</Menu.Item>
																))}
															</>
														)}
												</Menu.Dropdown>
											</Menu>
										)}
										{canModifyAccounts && (
											<>
												<Tooltip label="Edit Account">
													<ActionIcon
														variant="light"
														color="blue"
														onClick={() => onEditAccount(account)}>
														<IconEdit size={16} />
													</ActionIcon>
												</Tooltip>
												<Tooltip label="Delete Account">
													<ActionIcon
														variant="light"
														color="red"
														onClick={() =>
															onDeleteAccount(account._id, account.name)
														}>
														<IconTrash size={16} />
													</ActionIcon>
												</Tooltip>
											</>
										)}
									</Group>
								)}
							</Table.Td>
						</Table.Tr>
					))}
				</Table.Tbody>
			</Table>
		</Table.ScrollContainer>
	);
}

export default AccountTable;
